var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { generateSearchSessionId, selectSearchSessionId, selectPostLoginAttemptId, selectManualInstructionsAttemptId, generatePostLoginAttemptId, generateManualInstructionsAttemptId, updateEventSource as _updateEventSource, selectCurrentSource, selectLastSource, } from '../state/slices/slice-segment';
import { selectAmountSelection, setAmountSelection as _setAmountSelection, } from '../state/slices/slice-amount-selection';
import { getLocalizedStringFn } from '../consts/localized-strings';
import topVisibleImages from '../consts/visible-top-platforms';
import eventQueue from '../events/queue';
import { selectHistory } from '../state/history';
import { selectSelectedEmployer, selectSelectedPlatform, setSelectedEmployer as _setSelectedEmployer, setSelectedPlatform as _setSelectedPlatform, } from '../state/slices/slice-provider-entity';
import { changeLoadedPlatform, selectLoadedPlatform, selectLoginAttemptId, } from '../state/slices/slice-link-api';
import { getFireEventFn, getFireProperEventTypeFn, getUnifiedEventFn, irisTrack, } from '../tracking/iris';
import { EventAction } from '../types/events';
import useFireClientEvent from './useFireClientEvent';
import { noop } from '../utils';
function useApp(opts, linkTokenPayload, modalSessionId, _error, _open) {
    if (_error === void 0) { _error = null; }
    if (_open === void 0) { _open = true; }
    var dispatch = useDispatch();
    var history = useSelector(selectHistory);
    var selectedEmployer = useSelector(selectSelectedEmployer);
    var setSelectedEmployer = useCallback(function (employer) {
        dispatch(_setSelectedEmployer(employer));
    }, [dispatch]);
    var selectedPlatform = useSelector(selectSelectedPlatform);
    var setSelectedPlatform = useCallback(function (platform) {
        dispatch(_setSelectedPlatform(platform));
    }, [dispatch]);
    var loadedPlatform = useSelector(selectLoadedPlatform);
    var setLoadedPlatform = useCallback(function (platform) { return dispatch(changeLoadedPlatform(platform)); }, [dispatch]);
    var _a = useState(null), accountId = _a[0], setAccountId = _a[1];
    var _b = useState(_open), open = _b[0], setOpen = _b[1];
    var _c = useState(new Set()), checkpoints = _c[0], setCheckpoints = _c[1];
    var _d = useState(_error), error = _d[0], setError = _d[1];
    var _e = useState(null), linkParams = _e[0], setLinkParams = _e[1];
    var lastSource = useSelector(selectLastSource);
    var currentSource = useSelector(selectCurrentSource);
    var updateEventSource = useCallback(function (eventSource) { return dispatch(_updateEventSource(eventSource)); }, [dispatch]);
    var searchSessionId = useSelector(selectSearchSessionId);
    var setSearchSessionId = useCallback(function () { return dispatch(generateSearchSessionId); }, [dispatch]);
    var loginAttemptId = useSelector(selectLoginAttemptId);
    /*
     * Just leaving this to comply with previous interface, loginAttemptId's are generated
     * whenever we call LinkApi login initialization.
     */
    var setLoginAttemptId = noop;
    var postLoginAttemptId = useSelector(selectPostLoginAttemptId);
    var setPostLoginAttemptId = useCallback(function () { return dispatch(generatePostLoginAttemptId); }, [dispatch]);
    var manualInstructionsAttemptId = useSelector(selectManualInstructionsAttemptId);
    var setManualInstructionsAttemptId = useCallback(function () { return dispatch(generateManualInstructionsAttemptId); }, [dispatch]);
    var _f = useState(false), showingExitConfirmation = _f[0], setShowingExitConfirmation = _f[1];
    var _g = useState(false), hasAttemptedLogin = _g[0], setHasAttemptedLogin = _g[1];
    var _h = useState(null), lastEmittedPayload = _h[0], setLastEmittedPayload = _h[1];
    var _j = useState(), companyColor = _j[0], setCompanyColor = _j[1];
    var _k = useState(null), navigateBack = _k[0], setNavigateBack = _k[1];
    var amountSelection = useSelector(selectAmountSelection);
    var setAmountSelection = useCallback(function (newAmountSelection) {
        return dispatch(_setAmountSelection(newAmountSelection));
    }, [dispatch]);
    var _l = useState(null), linkApiResponse = _l[0], setLinkApiResponse = _l[1];
    var _m = useState([]), linkApiJobResults = _m[0], setLinkApiJobResults = _m[1];
    var addLinkApiJobResult = useMemo(function () { return function (result) {
        return setLinkApiJobResults(linkApiJobResults.concat(result));
    }; }, [linkApiJobResults]);
    var _o = useState(), directDepositAllocations = _o[0], setDirectDepositAllocations = _o[1];
    var _p = useState(topVisibleImages), topPlatformsImages = _p[0], setTopPlatformsImages = _p[1];
    var fireUnifiedEvent = getUnifiedEventFn(linkTokenPayload, modalSessionId, opts);
    var fireProperEventType = getFireProperEventTypeFn(fireUnifiedEvent);
    // TODO: Remove fireTrackEvent when we are fully migrated off
    // them to the new fireUnifiedEvent.
    var fireTrackEvent = getFireEventFn(irisTrack, linkTokenPayload, modalSessionId, selectedEmployer, opts, selectedPlatform, checkpoints);
    var getLocalizedString = getLocalizedStringFn(linkTokenPayload.language ? linkTokenPayload.language : 'en', linkTokenPayload.workspaceName);
    // todo: unclear on why opts.fireClientEvent is the fallback here
    var fireClientEvent = useFireClientEvent() || opts.fireClientEvent;
    var newOpts = useMemo(function () { return ({
        modalSessionId: modalSessionId,
        uniqueUserId: opts.uniqueUserId,
        version: opts.version,
        linkToken: opts.linkToken,
        loadedPlatform: loadedPlatform,
        setLoadedPlatform: setLoadedPlatform,
        selectedPlatform: selectedPlatform,
        setSelectedPlatform: setSelectedPlatform,
        error: error,
        setError: setError,
        open: open,
        setOpen: setOpen,
        checkpoints: checkpoints,
        setCheckpoints: setCheckpoints,
        accountId: accountId,
        setAccountId: setAccountId,
        linkTokenPayload: linkTokenPayload,
        setSelectedEmployer: setSelectedEmployer,
        selectedEmployer: selectedEmployer,
        fireUnifiedEvent: fireUnifiedEvent,
        fireProperEventType: fireProperEventType,
        fireTrackEvent: fireTrackEvent,
        fireClientEvent: fireClientEvent,
        linkParams: linkParams,
        setLinkParams: setLinkParams,
        lastSource: lastSource,
        currentSource: currentSource,
        updateEventSource: updateEventSource,
        searchSessionId: searchSessionId,
        setSearchSessionId: setSearchSessionId,
        loginAttemptId: loginAttemptId,
        setLoginAttemptId: setLoginAttemptId,
        postLoginAttemptId: postLoginAttemptId,
        setPostLoginAttemptId: setPostLoginAttemptId,
        manualInstructionsAttemptId: manualInstructionsAttemptId,
        setManualInstructionsAttemptId: setManualInstructionsAttemptId,
        showingExitConfirmation: showingExitConfirmation,
        setShowingExitConfirmation: setShowingExitConfirmation,
        hasAttemptedLogin: hasAttemptedLogin,
        setHasAttemptedLogin: setHasAttemptedLogin,
        lastEmittedPayload: lastEmittedPayload,
        setLastEmittedPayload: setLastEmittedPayload,
        companyColor: companyColor,
        setCompanyColor: setCompanyColor,
        topPlatformsImages: topPlatformsImages,
        getLocalizedString: getLocalizedString,
        featureFlags: opts.featureFlags,
        experiments: opts.experiments,
        dynamicConfigs: opts.dynamicConfigs,
        sdk: opts.sdk,
        directDepositAllocations: directDepositAllocations,
        setDirectDepositAllocations: setDirectDepositAllocations,
        amountSelection: amountSelection,
        setAmountSelection: setAmountSelection,
        linkApiResponse: linkApiResponse,
        setLinkApiResponse: setLinkApiResponse,
        linkApiJobResults: linkApiJobResults,
        addLinkApiJobResult: addLinkApiJobResult,
        navigateBack: navigateBack,
        setNavigateBack: setNavigateBack,
        isDemoMode: opts._demoModeEnabled || false,
        history: history,
        exit: noop,
    }); }, [
        modalSessionId,
        opts.uniqueUserId,
        opts.version,
        opts.linkToken,
        opts.featureFlags,
        opts.experiments,
        opts.dynamicConfigs,
        opts.sdk,
        opts._demoModeEnabled,
        loadedPlatform,
        setLoadedPlatform,
        selectedPlatform,
        setSelectedPlatform,
        error,
        open,
        checkpoints,
        accountId,
        linkTokenPayload,
        setSelectedEmployer,
        selectedEmployer,
        fireUnifiedEvent,
        fireProperEventType,
        fireTrackEvent,
        fireClientEvent,
        linkParams,
        lastSource,
        currentSource,
        updateEventSource,
        searchSessionId,
        setSearchSessionId,
        loginAttemptId,
        setLoginAttemptId,
        postLoginAttemptId,
        setPostLoginAttemptId,
        manualInstructionsAttemptId,
        setManualInstructionsAttemptId,
        showingExitConfirmation,
        hasAttemptedLogin,
        lastEmittedPayload,
        companyColor,
        topPlatformsImages,
        getLocalizedString,
        directDepositAllocations,
        amountSelection,
        setAmountSelection,
        linkApiResponse,
        linkApiJobResults,
        addLinkApiJobResult,
        navigateBack,
        history,
    ]);
    var exit = useCallback(function () {
        fireProperEventType(EventAction.EXIT_EXIT, newOpts);
        setOpen(false);
        eventQueue.emit('exit', {});
    }, [fireProperEventType, newOpts, setOpen]);
    return {
        opts: __assign(__assign({}, newOpts), { exit: exit }),
        otherValues: {
            setTopPlatformsImages: setTopPlatformsImages,
        },
    };
}
export default useApp;
